import React, { useState, useEffect, useCallback } from 'react';
import { Space, Switch, InputNumber, Input, Select, Button, Alert, Typography, Row, Col, message } from 'antd';
import { SaveOutlined, SendOutlined } from '@ant-design/icons';
import api from '../api';
import { useI18n } from '../i18n';

// 表单行：左侧说明 + 右侧控件（与自动交易面板一致）
const FieldRow = ({ label, hint, children }) => (
  <Row align="middle" gutter={16}>
    <Col span={7}>
      <Typography.Text>{label}</Typography.Text>
      {hint && (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {hint}
          </Typography.Text>
        </div>
      )}
    </Col>
    <Col span={17}>{children}</Col>
  </Row>
);

// 价格提醒设置：开关、上下限、webhook 接口/Key/接收方、冷却时长、测试发送
const AlertPanel = () => {
  const { t, ts } = useI18n();
  const [config, setConfig] = useState(null);
  const [apiKeySet, setApiKeySet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get('/alert/config');
      setConfig(res.data.data);
      setApiKeySet(res.data.data.apiKeySet);
    } catch (err) {
      setError(ts(err.response?.data?.error || err.message));
    }
  }, [ts]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (field) => (value) => setConfig((c) => ({ ...c, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/alert/config', config);
      setConfig(res.data.data);
      setApiKeySet(res.data.data.apiKeySet);
      message.success(res.data.data.enabled ? t('已保存，价格提醒已开启') : t('已保存，价格提醒已关闭'));
    } catch (err) {
      message.error(t('保存失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await api.post('/alert/test');
      message.success(t('测试消息已发送，请检查接收端'));
    } catch (err) {
      message.error(t('测试发送失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setTesting(false);
    }
  };

  if (!config) {
    return error ? <Alert type="error" showIcon message={error} /> : <Typography.Text type="secondary">{t('加载中…')}</Typography.Text>;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 760 }}>
      <Alert
        type={config.enabled ? 'success' : 'info'}
        showIcon
        message={config.enabled ? t('价格提醒已开启：每分钟检查池内价，越界时发送 webhook') : t('价格提醒已关闭：填好配置并打开开关后生效')}
        description={t('池内价高于上限或低于下限时，向配置的接口 POST 一条英文告警；同方向持续越界按冷却时长去重，回到区间内后再次越界会重新提醒')}
      />

      <FieldRow label={t('提醒开关')}>
        <Switch checked={config.enabled} onChange={set('enabled')} checkedChildren={t('开_ON')} unCheckedChildren={t('关_OFF')} />
      </FieldRow>

      <FieldRow label={t('提醒价格区间')} hint={t('1 USGNAA = ? USDT；超出区间即触发提醒')}>
        <Space>
          <InputNumber min={0} step={0.01} value={config.priceMin} onChange={set('priceMin')} addonBefore={t('下限')} />
          <span>~</span>
          <InputNumber min={0} step={0.01} value={config.priceMax} onChange={set('priceMax')} addonBefore={t('上限')} />
        </Space>
      </FieldRow>

      <FieldRow label={t('接口地址')} hint={t('接收 POST 的 webhook 地址，如 http://localhost:8000/api/v1/send')}>
        <Input
          value={config.apiUrl}
          onChange={(e) => set('apiUrl')(e.target.value)}
          placeholder="http://localhost:8000/api/v1/send"
          allowClear
        />
      </FieldRow>

      <FieldRow label={t('接口 Key')} hint={t('作为 X-Api-Key 请求头发送；留空表示保留已存 Key 不修改')}>
        <Input.Password
          value={config.apiKey}
          onChange={(e) => set('apiKey')(e.target.value)}
          placeholder={apiKeySet ? t('已设置（留空则不修改）') : t('填写接口 Key')}
          allowClear
        />
      </FieldRow>

      <FieldRow label={t('接收方')} hint={t('recipients 数组，如 group.xxx；输入后回车添加，可多个')}>
        <Select
          mode="tags"
          style={{ width: '100%' }}
          value={config.recipients}
          onChange={set('recipients')}
          placeholder={t('输入接收方标识后回车')}
          tokenSeparators={[',', '，', ' ']}
          allowClear
        />
      </FieldRow>

      <FieldRow label={t('重复提醒冷却')} hint={t('同方向持续越界时，间隔多久才再次提醒（分钟）')}>
        <InputNumber min={0} max={1440} value={config.cooldownMinutes} onChange={set('cooldownMinutes')} addonAfter={t('分钟')} style={{ width: 160 }} />
      </FieldRow>

      <Space>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          {t('保存配置')}
        </Button>
        <Button icon={<SendOutlined />} onClick={handleTest} loading={testing}>
          {t('发送测试消息')}
        </Button>
      </Space>

      {error && <Alert type="error" showIcon message={error} />}
    </Space>
  );
};

export default AlertPanel;
